// PARTNER-PORTÁL · Profil — a terv (a partner-terv szerint — a MEGÉPÜLT állapot: `docs/foundations/design.md` §9) szakaszai:
// Személyes adatok · Jelszó és biztonság · Értesítések · Fiókjaim · Jogi tudnivalók.
//
// AMIT ÚJRAHASZNÁL (nincs második implementáció): a `ProfileDetailsForm` (partner-módban: becenév és
// integrációk nélkül, csak olvasható e-maillel), a `PasswordCard` és a `TwoFactorCard` — mind self-service
// komponens, ami a hívó SAJÁT identityjén dolgozik, tehát felület-független.
//
// AMI A TERVBŐL NEM MEGVALÓSÍTHATÓ MA: a terv ötsoros, típusonkénti értesítés-mátrixa (jegy-válasz ·
// státusz-váltás · számla · üzenet · megbeszélés × e-mail/app). Az Odin `identity_profiles`-ában ma KÉT
// GLOBÁLIS kapcsoló van (`notify_email`, `notify_in_app`) — típusonkénti preferencia nincs. Ötsoros mátrixot
// mutatni, ami mögött két kapcsoló áll, hazugság lenne: ezért a két valódi kapcsoló látszik, a hiányzó
// finomhangolás pedig nyitott tétel (`docs/ROADMAP.md` A-132).
import {
  BuildingOffice2Icon,
  DocumentTextIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { CardRow, CardRows } from '@/components/shared/card-rows';
import { DomainBadge } from '@/components/shared/domain-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { FormSection } from '@/components/shared/form-section';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { usePortal, usePortalHead } from '@/features/portal/portal-context';
import { profileKeys } from '@/features/profile/api';
import { PasswordCard } from '@/features/profile/components/password-card';
import { ProfileDetailsForm } from '@/features/profile/components/profile-details-form';
import { TwoFactorCard } from '@/features/profile/components/two-factor-card';
import { api } from '@/lib/api';
import { formatName } from '@/lib/format-name';

export const Route = createFileRoute('/portal/profile')({
  component: PortalProfilePage,
});

function PortalProfilePage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { me } = usePortal();
  usePortalHead({ title: t('portal.nav.profile'), subtitle: t('portal.profile.subtitle') }, [
    t('portal.nav.profile'),
  ]);

  const query = api.profile.useQuery(profileKeys.detail(), {});
  const profile = query.data?.status === 200 ? query.data.body : undefined;

  if (query.isLoading) {
    return (
      <div className="w-full max-w-3xl space-y-5">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (!profile) {
    return (
      <div className="w-full max-w-3xl">
        <EmptyState title={t('profile.loadErrorTitle')} description={t('profile.loadErrorDesc')} />
      </div>
    );
  }

  const name = formatName(
    {
      givenName: profile.givenName,
      familyName: profile.familyName,
      displayName: profile.displayName ?? profile.email,
    },
    i18n.language,
  );
  const primaryCount = (me?.accounts ?? []).filter((a) => a.isPrimary).length;

  return (
    <div className="w-full max-w-3xl space-y-8">
      {/* SZEMÉLYES ADATOK — a terv fejléce: nagy avatar + név + ügyfél + a két jelölés (elsődleges · fiókok). */}
      <section>
        <div className="mb-5 flex items-center gap-3.5">
          <Avatar name={name} src={profile.avatarUrl} size="xl" />
          <div className="flex min-w-0 flex-col gap-0.5">
            <Text variant="title" as="span">
              {name}
            </Text>
            <Text variant="secondary" as="span" className="truncate">
              {me?.accounts[0]?.clientName ?? ''}
            </Text>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {primaryCount > 0 && (
                <DomainBadge
                  kind="marker"
                  value="primary"
                  label={t('portal.profile.primaryBadge')}
                />
              )}
              <Badge variant="muted" icon={<BuildingOffice2Icon />}>
                {t('portal.profile.accountCount', { count: me?.accounts.length ?? 0 })}
              </Badge>
            </div>
          </div>
        </div>
        <ProfileDetailsForm profile={profile} surface="partner" />
      </section>

      <PasswordCard />
      <TwoFactorCard mfaEnabled={profile.mfaEnabled} twoFactorPending={profile.twoFactorPending} />

      {/* FIÓKJAIM — csak OLVASHATÓ: a hozzáférés-osztás ügyintézői/elsődleges-kapcsolattartói döntés. */}
      <FormSection
        title={t('portal.shell.myAccounts')}
        description={t('portal.profile.accountsHint')}
      >
        <Card>
          <CardRows>
            {(me?.accounts ?? []).map((a) => (
              <CardRow key={a.id} className="flex items-center gap-3 px-4 py-2.5">
                <span className="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-[9px]">
                  <BuildingOffice2Icon className="size-4" />
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <Text variant="itemTitle" as="span" className="truncate">
                    {a.name}
                  </Text>
                  <Text variant="secondary" as="span" className="truncate">
                    {a.clientName ?? '—'}
                  </Text>
                </span>
                {a.isPrimary && (
                  <DomainBadge
                    kind="marker"
                    value="primary"
                    label={t('portal.profile.primaryShort')}
                  />
                )}
                <DomainBadge
                  kind="portalRole"
                  value={a.role}
                  label={t(`portalAccess.roles.${a.role}`)}
                />
              </CardRow>
            ))}
          </CardRows>
        </Card>
      </FormSection>

      {/* JOGI TUDNIVALÓK — a terv szerint innen (is) elérhető a két statikus lap. */}
      <FormSection title={t('portal.profile.legalTitle')}>
        <Card>
          <CardRows>
            {(
              [
                [
                  '/portal/legal/imprint',
                  DocumentTextIcon,
                  'portal.nav.imprint',
                  'portal.profile.imprintSub',
                ],
                [
                  '/portal/legal/gdpr',
                  ShieldCheckIcon,
                  'portal.nav.gdpr',
                  'portal.profile.gdprSub',
                ],
              ] as const
            ).map(([to, Icon, titleKey, subKey]) => (
              <CardRow key={to}>
                <Link
                  to={to}
                  className="hover:bg-accent flex items-center gap-3 px-4 py-2.5 transition-colors"
                >
                  <span className="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-[9px]">
                    <Icon className="size-4" />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <Text variant="itemTitle" as="span">
                      {t(titleKey)}
                    </Text>
                    <Text variant="secondary" as="span" className="truncate">
                      {t(subKey)}
                    </Text>
                  </span>
                </Link>
              </CardRow>
            ))}
          </CardRows>
        </Card>
      </FormSection>
    </div>
  );
}
